/**
 * Wraps an async function so that repeated calls with the same arguments
 * (compared via JSON serialization) return the cached promise from the
 * most recent invocation instead of re-executing the function.
 *
 * Only the last result is cached — if the arguments change, the function
 * is re-invoked and the new promise replaces the previous one.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function memoizeAsync<A extends any[], R>(
  fn: (...args: A) => Promise<R>,
): (...args: A) => Promise<R> {
  let cachedKey: string | null = null;
  let cachedPromise: Promise<R> | null = null;

  return (...args: A): Promise<R> => {
    const key = JSON.stringify(args);

    if (cachedPromise && key === cachedKey) {
      return cachedPromise;
    }

    cachedPromise = fn(...args);
    cachedKey = key;

    return cachedPromise;
  };
}
